import React, { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { Monitor, Bell, BellRing, Loader, AlertTriangle, X, Check } from "lucide-react";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { useRetailer } from "../../hooks/useRetailer";
import { useUserContext } from "../../context/UserContext";
import { useTranslation } from "../../context/LanguageContext";
import { useTrackedProductsWithRetailer } from "../../hooks/useTracking";
import { getTrackerLimit, type WatchRules } from "../../services/TrackingService";
import ProductSearchTrackingModal from "../ProductSearchTrackingModal";
import { getTrackerMeta } from "./SetAlertButton.utils";
import UpgradeModal from "../UpgradeModal";
import { isFreeTier } from "../../utils/userUtils";

interface SetAlertButtonProps {
  productUpc: string;
  productSku?: string;
  productName: string;
  productImage?: string;
  productCategory?: string;
  msrp?: number;
  currentPrice?: number;
  className?: string;
  /** Icon shown before an alert is set. Defaults to the monitor used across the app. */
  icon?: "monitor" | "bell";
}

const SetAlertButton: React.FC<SetAlertButtonProps> = ({
  productUpc,
  productSku,
  productName,
  productImage,
  productCategory,
  msrp,
  currentPrice,
  className = "",
  icon = "monitor",
}) => {
  const currentRetailer = useRetailer();
  const { user } = useUserContext();
  const { t } = useTranslation();
  const isFreeUser = isFreeTier(user);
  const { data: trackers = [] } = useTrackedProductsWithRetailer(currentRetailer);
  const trackerLimit = getTrackerLimit(user?.accountType, user?.maxAlertsOverride);

  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isTrackingProduct, setIsTrackingProduct] = useState(false);

  const trackerCount = trackers.length;
  const { isAtLimit } = getTrackerMeta(trackerCount, trackerLimit);

  const isAlreadyTracked = useMemo(() => trackers.some(t => {
    const baseUpc = t.upc.split("#")[0];
    return t.upc === productUpc || t.actualUpc === productUpc || baseUpc === productUpc;
  }), [trackers, productUpc]);

  const handleTrackClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isFreeUser) {
      setShowUpgradeModal(true);
      return;
    }
    if (isAlreadyTracked) {
      toast.info(t("alerts.alreadyTracked"));
      return;
    }
    if (isAtLimit) {
      setShowLimitModal(true);
    } else {
      setShowTrackingModal(true);
    }
  };

  const handleAddTracker = async (data: {
    upc: string;
    zipCode: string;
    targetPrice: number;
    productName?: string;
    productImage?: string;
    productCategory?: string;
    msrp?: number;
    currentPrice?: number;
    rules?: WatchRules;
  }) => {
    try {
      setIsTrackingProduct(true);
      const { addTrackerWithRetailer } = await import("../../services/TrackingService");

      // For Walmart: try UPC first, fallback to SKU on error
      if (currentRetailer === "walmart" && productSku) {
        try {
          await addTrackerWithRetailer(data, currentRetailer, user?.accountType, user?.maxAlertsOverride);
          toast.success(t("alerts.trackerAdded"));
          setShowTrackingModal(false);
          return;
        } catch {
          // Retry with SKU
          await addTrackerWithRetailer({ ...data, upc: productSku }, currentRetailer, user?.accountType, user?.maxAlertsOverride);
          toast.success(t("alerts.trackerAdded"));
          setShowTrackingModal(false);
          return;
        }
      }

      await addTrackerWithRetailer(data, currentRetailer, user?.accountType, user?.maxAlertsOverride);
      toast.success(t("alerts.trackerAdded"));
      setShowTrackingModal(false);
    } catch (error: any) {
      if (error.status === 409) {
        toast.info(t("alerts.alreadyTracked"));
      } else {
        toast.error(error.message || t("alerts.trackerAddFailed"));
      }
    } finally {
      setIsTrackingProduct(false);
    }
  };

  return (
    <>
      <button
        onClick={handleTrackClick}
        disabled={isTrackingProduct || isAlreadyTracked}
        aria-label={isTrackingProduct ? t("alerts.adding") : isAlreadyTracked ? t("alerts.tracking") : t("alerts.alert")}
        className={`flex items-center justify-center gap-1.5 h-9 px-2.5 rounded-xl text-xs font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
          isAlreadyTracked
            ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 cursor-default"
            : isTrackingProduct
            ? "bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 cursor-not-allowed"
            : "bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/60 active:scale-95"
        } ${className}`}
      >
        {isTrackingProduct ? (
          <Loader className="w-3 h-3 animate-spin" />
        ) : isAlreadyTracked ? (
          icon === "bell" ? <BellRing className="w-3 h-3" /> : <Check className="w-3 h-3" />
        ) : (
          icon === "bell" ? <Bell className="w-3 h-3" /> : <Monitor className="w-3 h-3" />
        )}
        <span>{isTrackingProduct ? t("alerts.adding") : isAlreadyTracked ? t("alerts.tracking") : t("alerts.alert")}</span>
      </button>

      {/* Tracking Modal */}
      {showTrackingModal &&
        createPortal(
          <ProductSearchTrackingModal
            isOpen={showTrackingModal}
            onClose={() => setShowTrackingModal(false)}
            onSubmit={handleAddTracker}
            isLoading={isTrackingProduct}
            productUpc={productUpc}
            searchZipCode={user?.zipCode || ""}
            productName={productName}
            productImage={productImage}
            productCategory={productCategory}
            msrp={msrp}
            currentPrice={currentPrice}
            trackerCount={trackerCount}
            trackerLimit={trackerLimit}
          />,
          document.body
        )}

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title={t("alerts.upgradeTitle")}
        description={t("alerts.upgradeDescription", { productName })}
        highlights={[
          t("alerts.upgradeHighlightNotify"),
          t("alerts.upgradeHighlightMonitor"),
          t("alerts.upgradeHighlightLocal"),
        ]}
        ctaLabel={t("alerts.unlockAlerts")}
      />

      {/* Limit Reached Modal */}
      {showLimitModal &&
        createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center z-[9999] p-4 bg-black/50 dark:bg-black/70"
            onClick={() => setShowLimitModal(false)}
          >
            <div
              className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header with warning gradient */}
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-4 relative">
                <button
                  onClick={() => setShowLimitModal(false)}
                  className="absolute right-3 top-3 p-1.5 text-white/80 hover:text-white hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">{t("alerts.limitTitle")}</h2>
                    <p className="text-white/80 text-xs">{t("alerts.limitSubtitle")}</p>
                  </div>
                </div>
              </div>

              {/* Usage display */}
              <div className="px-5 py-4 bg-gray-50 dark:bg-dark-700 border-b border-gray-100 dark:border-dark-600">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-dark-200">{t("alerts.usageLabel")}</span>
                  <span className="text-sm font-bold text-amber-600 dark:text-amber-400">{trackerCount} / {trackerLimit}</span>
                </div>
                <div className="h-3 bg-gray-200 dark:bg-dark-600 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-amber-500 to-red-500 rounded-full w-full" />
                </div>
                <p className="text-[11px] text-gray-500 dark:text-dark-400 mt-2">
                  {t("alerts.noSlotsAvailable")}
                </p>
              </div>

              {/* Message */}
              <div className="p-5">
                <p className="text-gray-600 dark:text-dark-300 text-sm mb-4">
                  {t("alerts.limitUsingAll")}{" "}
                  <span className="font-bold text-gray-900 dark:text-white">{t("alerts.limitPriceAlerts", { limit: trackerLimit })}</span>.{" "}
                  {t("alerts.limitRemoveFirst")}
                </p>

                <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800 rounded-lg p-3 mb-4">
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    <span className="font-semibold">{t("alerts.tipLabel")}</span> {t("alerts.tipText")}
                  </p>
                </div>

                <div className="flex gap-2">
                  <Link
                    to="/tracks"
                    onClick={() => setShowLimitModal(false)}
                    className="flex-1 py-3 bg-primary-600 text-white rounded-xl font-semibold text-center hover:bg-primary-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <Monitor className="w-4 h-4" />
                    {t("alerts.manageMyAlerts")}
                  </Link>
                </div>
                <button
                  onClick={() => setShowLimitModal(false)}
                  className="w-full mt-2 py-2 text-gray-500 dark:text-dark-400 text-sm hover:text-gray-700 dark:hover:text-dark-200 transition-colors"
                >
                  {t("common.cancel")}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default SetAlertButton;
